import { useQuery } from '@apollo/client';
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { Tab, Tabs } from '@gamma/ui/tabs';
import DownloadSharpIcon from '@gamma/ui/icons/DownloadSharpIcon';
import HelpOutlinedIcon from '@gamma/ui/icons/HelpOutlinedIcon';
import {
  Board,
  Column,
  FlushPanelContent,
  Panel,
  PanelHeader,
  PanelHeading,
} from '@gamma/ui/layout';
import { useContext, useMemo, useState, useEffect } from 'react';
import {
  useDateRangeContext,
  useDateRangeQueryString,
} from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import { OrgTenantsContext } from '@investigator/shared/providers';
import {
  AlertCategoriesDetectionsTable,
  HighRiskEntitiesDetectionsTable,
} from '../../../detections/components';
import {
  GET_DETECTION_MITRE_SUMMARY,
  GET_DETECTION_PERIOD_SUMMARY,
  IGetDetectionMitreSummary,
  IGetDetectionPeriodSummary,
} from '../../../detections/queries';

import { PDFDownloadPopoverContent } from './PDFDownloadPopoverContent';

import { useAgenticTriageSettings } from '../../hooks';
import {
  DetectionsByCategoryPanel,
  EntitiesWithDetectionsPanel,
  TotalDetectionsPanel,
} from '..';
import { HighestRiskEntitiesTable } from '../../components';
import {
  EntityTableContext,
  useEntityTableContext,
} from '../../context/EntityTableContext';
import {
  DateSelectSync,
  getDateRangeOptionFromDates,
} from '@investigator/shared/components';
import { useLocation, useNavigate } from 'react-router';
import RefreshSharpIcon from '@gamma/ui/icons/RefreshSharpIcon';
import moment from 'moment';
import { GraphQLReqStatus } from '@gamma/progress';

const { includesOpen, panels } = i18n.pages.securityOverview;

const { title, subTitle, tooltip } =
  i18n.pages.entities.highestRiskEntitiesTable;
const { refresh, refreshTriagePageToolTip } = i18n.dateSelectSync;

const SecurityOverviewContent: React.FC = () => {
  const { agenticTriageFeatureEnabled } = useAgenticTriageSettings();
  const { orgTenantsQueryParam } = useContext(OrgTenantsContext);
  const { start, end } = useDateRangeQueryString();
  const { search, pathname } = useLocation();
  const { dateRange } = useDateRangeContext();

  const params = useMemo(() => new URLSearchParams(search), [search]);
  const entityTableContext = useEntityTableContext();
  const navigate = useNavigate();

  // Always call hooks first
  const shouldSkip = !start || !end || !orgTenantsQueryParam?.length;

  const {
    loading: summaryLoading,
    error: summaryError,
    data: summaryData,
    stopPolling: stopSummaryPolling,
  } = useQuery<IGetDetectionPeriodSummary>(GET_DETECTION_PERIOD_SUMMARY, {
    skip: shouldSkip,
    variables: {
      start,
      end,
      prev_start: start - (end - start),
      prev_end: start,
      must_conds: [
        `{"terms": {"tenant": ${JSON.stringify(orgTenantsQueryParam)}}}`,
      ],
    },
    pollInterval: shouldSkip ? undefined : 2000,
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: mitreData,
    loading: mitreLoading,
    error: mitreError,
    stopPolling: stopMitrePolling,
  } = useQuery<IGetDetectionMitreSummary>(GET_DETECTION_MITRE_SUMMARY, {
    skip: shouldSkip,
    variables: {
      start,
      end,
      must_conds: [
        `{"terms": {"tenant": ${JSON.stringify(orgTenantsQueryParam)}}}`,
        '{"term": {"detection_status": "open"}}',
      ],
    },
    pollInterval: shouldSkip ? undefined : 2000,
    notifyOnNetworkStatusChange: true,
  });

  // Stop polling when data is loaded
  useEffect(() => {
    if (summaryData) {
      stopSummaryPolling();
    }
  }, [summaryData, stopSummaryPolling]);

  useEffect(() => {
    if (mitreData) {
      stopMitrePolling();
    }
  }, [mitreData, stopMitrePolling]);

  // Transform entity table context data for PDF
  const highRiskData = useMemo(() => {
    if (
      !entityTableContext.tableData ||
      entityTableContext.tableData.length === 0
    ) {
      return null;
    }
    return {
      summaries: entityTableContext.tableData.map((entity) => ({
        severity: entity.max_severity ?? 0,
        entity_name: entity.entity_name ?? '',
        detection_count: entity.total_detections ?? 0,
        entity_category: entity.entity_category ?? 'source',
        entity_type: entity.entity_type ?? '',
        total_detections: entity.total_detections ?? 0,
        is_custom_severity: false, // Not available in insights data
      })),
      total_items: entityTableContext.totalItems,
    };
  }, [entityTableContext.tableData, entityTableContext.totalItems]);

  // Add back the state
  const [alertCategoriesData, setAlertCategoriesData] = useState<{
    categories: { severity: number; name: string; detectionCount: number }[];
    totalItems: number;
  } | null>(null);

  const refreshButtonOnClick = () => {
    const range = getDateRangeOptionFromDates(start, end) ?? dateRange;
    if (!range) return;

    const [amount, unit] = range;
    const newEnd = `${moment().unix()}`;
    const newStart = `${moment().subtract(amount, unit).unix()}`;
    params.set('end', newEnd);
    params.set('start', newStart);

    navigate({
      pathname,
      search: params.toString(),
    });
  };

  const [tabIndex, setTabIndex] = useState(
    Number(localStorage.getItem('security-overview-active-table') ?? 0),
  );

  const tabs = useMemo(
    () => [
      {
        title: panels.highestRiskEntities.tabHeader,
        content: (
          <div data-testid="highest-risk-entities">
            <HighRiskEntitiesDetectionsTableWithData />
          </div>
        ),
      },
      {
        title: panels.alertCategories,
        content: (
          <div data-testid="alert-categories">
            <AlertCategoriesDetectionsTable
              onDataLoad={setAlertCategoriesData}
            />
          </div>
        ),
      },
    ],
    [setAlertCategoriesData],
  );

  // Guard: Wait for orgTenantsQueryParam to be available or show query status
  if (!orgTenantsQueryParam || orgTenantsQueryParam.length === 0) {
    return <GraphQLReqStatus loading={true} error={undefined} />;
  }
  if (summaryError) {
    return <GraphQLReqStatus loading={false} error={summaryError} />;
  }

  return (
    <Board h="100%" flex="1 1 auto" height="inherit" margin={0} width="auto">
      <Popover placement="bottom-end">
        <PopoverTrigger>
          <IconButton
            position="fixed"
            bottom={4}
            right={4}
            zIndex="overlay"
            aria-label="Download PDF"
            icon={<DownloadSharpIcon />}
            colorScheme="blue"
            rounded="full"
            size="lg"
            boxShadow="lg"
          />
        </PopoverTrigger>
        <PopoverContent width="auto">
          <PDFDownloadPopoverContent
            summaryLoading={summaryLoading}
            mitreLoading={mitreLoading}
            summaryError={summaryError}
            mitreError={mitreError}
            summaryData={summaryData}
            mitreData={mitreData}
            alertCategoriesData={alertCategoriesData}
            highRiskData={highRiskData}
            start={start}
            end={end}
          />
        </PopoverContent>
      </Popover>
      {agenticTriageFeatureEnabled ? (
        <Column
          display="flex"
          alignItems="start"
          col={{ base: 12, lg: 12 }}
          flex="0 0 auto"
          w="100%"
        >
          <Board w="100%" flexDir="column" flex="0 0 auto">
            <Column>
              <Panel mb={4}>
                <PanelHeader>
                  <PanelHeading>
                    <HStack>
                      <Text>{title}</Text>
                      <Tooltip label={tooltip} shouldWrapChildren>
                        <HelpOutlinedIcon
                          color="text.secondary"
                          data-testid="highest-risk-detections-info-label"
                        />
                      </Tooltip>
                    </HStack>
                    <HStack mt={4}>
                      <Text textStyle="body-md">{subTitle}</Text>
                    </HStack>
                  </PanelHeading>
                </PanelHeader>
                <FlushPanelContent>
                  <VStack w="100%" spacing={1} alignItems="start">
                    <Panel border={'none'}>
                      <HighestRiskEntitiesTable />
                    </Panel>
                  </VStack>
                </FlushPanelContent>
              </Panel>
            </Column>
          </Board>
        </Column>
      ) : null}
      {/* Date Range Selector for cards under agentic triage table */}
      <Column display="flex" justifyContent="end" col={12} mb={2}>
        <DateSelectSync
          navigate={navigate}
          dateSelectButtonConfig={{
            iconButtonProps: {
              'aria-label': refreshTriagePageToolTip,
              colorScheme: 'gray',
              borderRadius: '4px 0px 0px 4px',
              variant: 'solid',
              icon: <RefreshSharpIcon />,
              onClick: refreshButtonOnClick,
            },
            toolTipProps: {
              label: (
                <Text data-testid="refresh-button-refresh-text">{refresh}</Text>
              ),
              'aria-label': refreshTriagePageToolTip,
              placement: 'top',
            },
          }}
        />
      </Column>
      {/* END - Date Range Selecctor */}
      <Column col={{ base: 12 }} w="100%" flex="1 1 auto" minW={0}>
        <VStack w="100%" align="stretch" spacing={4}>
          {/* Row 1: Highest Severity and MITRE same height; row 2: two small cards left, dead space right */}
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            gap={4}
            align="stretch"
            w="100%"
            minW={0}
          >
            <Box flex={{ base: '1 1 auto', lg: '3.5 1 0' }} minW={0}>
              <Panel>
                <PanelHeader mb={8}>
                  <PanelHeading>
                    <HStack>
                      <Text>
                        {agenticTriageFeatureEnabled
                          ? panels.highestSeverityAlertCategories
                              .alertCategoriesHeader
                          : panels.highestSeverityAlertCategories
                              .entitiesHeader}
                      </Text>
                      <Tooltip label={includesOpen} shouldWrapChildren>
                        <HelpOutlinedIcon
                          color="text.secondary"
                          data-testid="highest-risk-detections-info-label"
                        />
                      </Tooltip>
                    </HStack>
                  </PanelHeading>
                </PanelHeader>
                <FlushPanelContent>
                  <div data-testid="top-entities-table">
                    {agenticTriageFeatureEnabled ? (
                      <div data-testid="alert-categories">
                        <AlertCategoriesDetectionsTable
                          onDataLoad={setAlertCategoriesData}
                        />
                      </div>
                    ) : (
                      <Tabs
                        variant="line"
                        defaultIndex={tabIndex}
                        onChange={(index) => {
                          setTabIndex(index);
                          localStorage.setItem(
                            'security-overview-active-table',
                            String(index),
                          );
                        }}
                      >
                        {tabs.map((tab) => (
                          <Tab key={tab.title} title={tab.title}>
                            <Box pt={2.5}>{tab.content}</Box>
                          </Tab>
                        ))}
                      </Tabs>
                    )}
                  </div>
                </FlushPanelContent>
              </Panel>
            </Box>
            <Box
              flex={{ base: '1 1 auto', lg: '8.5 1 0' }}
              minW={0}
              data-testid="mitre-attack-map"
              alignSelf="stretch"
              display="flex"
              flexDir="column"
            >
              <DetectionsByCategoryPanel
                loading={mitreLoading}
                error={mitreError}
                data={mitreData}
              />
            </Box>
          </Flex>
          <Flex
            direction={{ base: 'column', lg: 'row' }}
            gap={4}
            align="stretch"
            w="100%"
            minW={0}
          >
            <Box
              flex={{
                base: '1 1 auto',
                lg: '0 0 calc((100% - 16px) * 3.5 / 12)',
              }}
              minW={0}
              data-testid="entities-with-alerts-list"
            >
              <EntitiesWithDetectionsPanel
                loading={summaryLoading}
                error={summaryError}
                data={summaryData}
              />
            </Box>
            <Box
              flex={{
                base: '1 1 auto',
                lg: '0 0 calc((100% - 16px) * 3.5 / 12)',
              }}
              minW={0}
              data-testid="total-alert-categories"
            >
              <TotalDetectionsPanel
                loading={summaryLoading}
                error={summaryError}
                data={summaryData}
              />
            </Box>
            <Box flex={{ base: '0 0 0', lg: '1 1 0' }} minW={0} />
          </Flex>
        </VStack>
      </Column>
    </Board>
  );
};

export const SecurityOverview = () => {
  return (
    <EntityTableContext>
      <SecurityOverviewContent />
    </EntityTableContext>
  );
};

function HighRiskEntitiesDetectionsTableWithData(
  props: React.ComponentProps<typeof HighRiskEntitiesDetectionsTable>,
) {
  return <HighRiskEntitiesDetectionsTable {...props} />;
}
